import React, { useState, useEffect } from 'react';
import { organizationApi, Organization, OrganizationCreate } from '../api/organizations';
import { componentApi, Component } from '../api/components';
import { storeApi, Store } from '../api/stores';
import { pageApi, PageWithComponents } from '../api/pages';

interface OrganizationsProps {
  userInfo?: any;
}

const Organizations: React.FC<OrganizationsProps> = ({ userInfo }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [dashboardPages, setDashboardPages] = useState<PageWithComponents[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [formData, setFormData] = useState<OrganizationCreate>({ org_id: '', name: '', description: '' });
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [orgsData, compsData, storesData, pagesData] = await Promise.all([
        organizationApi.list(),
        componentApi.list(),
        storeApi.list(0, 1000),
        pageApi.getPagesWithComponents()
      ]);
      setOrganizations(orgsData);
      setComponents(compsData);
      setStores(storesData);
      setDashboardPages(pagesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await organizationApi.create(formData);
      setShowCreateModal(false);
      setFormData({ org_id: '', name: '', description: '' });
      loadData();
    } catch (error) {
      console.error('Error creating organization:', error);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) return;
    
    try {
      await organizationApi.update(selectedOrg.org_id, formData);
      setShowEditModal(false);
      setFormData({ org_id: '', name: '', description: '' });
      setSelectedOrg(null);
      loadData();
    } catch (error) {
      console.error('Error updating organization:', error);
    }
  };

  const handleDisable = async (org_id: string) => {
    if (window.confirm('Are you sure you want to disable this organization?')) {
      try {
        await organizationApi.disable(org_id);
        loadData();
      } catch (error) {
        console.error('Error disabling organization:', error);
      }
    }
  };

  const handlePermissionsUpdate = async (storeList: string[], configData: any) => {
    if (!selectedOrg) return;
    
    try {
      // Check if config exists to determine whether to create or update
      let configExists = false;
      try {
        await organizationApi.getConfig(selectedOrg.org_id);
        configExists = true;
      } catch (error) {
        // Config doesn't exist, will create new one
        configExists = false;
      }

      const payload = {
        org_id: selectedOrg.org_id,
        store_list: storeList,
        config_data: configData
      };

      if (configExists) {
        // Update existing config
        await organizationApi.updateConfig(selectedOrg.org_id, payload);
      } else {
        // Create new config
        await organizationApi.createConfig(selectedOrg.org_id, payload);
      }
      
      setShowPermissionsModal(false);
      setSelectedOrg(null);
      loadData();
    } catch (error) {
      console.error('Error updating permissions:', error);
    }
  };

  const openEditModal = (org: Organization) => {
    setSelectedOrg(org);
    setFormData({ org_id: org.org_id, name: org.name, description: org.description || '' });
    setShowEditModal(true);
  };

  const openPermissionsModal = (org: Organization) => {
    setSelectedOrg(org);
    setShowPermissionsModal(true);
  };

  // Filter organizations based on search term
  const filteredOrganizations = organizations.filter(org => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      org.org_id.toLowerCase().includes(searchLower) ||
      org.name.toLowerCase().includes(searchLower) ||
      (org.description && org.description.toLowerCase().includes(searchLower))
    );
  });

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900">Organizations</h1>
          <p className="mt-2 text-gray-600">
            Manage organizations and their access permissions
          </p>
        </div>
        {userInfo?.is_admin && (
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Add Organization
            </button>
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search organizations by name, ID, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrganizations.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                        {searchTerm ? 'No organizations found matching your search.' : 'No organizations found.'}
                      </td>
                    </tr>
                  ) : (
                    filteredOrganizations.map((org) => (
                    <tr key={org.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {org.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {org.description || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          org.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {org.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(org.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openPermissionsModal(org)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Permissions
                          </button>
                          {userInfo?.is_admin && (
                            <>
                              <button
                                onClick={() => openEditModal(org)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDisable(org.org_id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Disable
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create Organization</h3>
              <form onSubmit={handleCreate}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization ID
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.org_id}
                    onChange={(e) => setFormData({ ...formData, org_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., rjcorp, test_org_001"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedOrg && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Organization</h3>
              <form onSubmit={handleUpdate}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedOrg && (
        <PermissionsModal
          organization={selectedOrg}
          components={components}
          stores={stores}
          dashboardPages={dashboardPages}
          onSave={handlePermissionsUpdate}
          onClose={() => setShowPermissionsModal(false)}
        />
      )}
    </div>
  );
};

// Permissions Modal Component
interface PermissionsModalProps {
  organization: Organization;
  components: Component[];
  stores: Store[];
  dashboardPages: PageWithComponents[];
  onSave: (storeList: string[], configData: any) => void;
  onClose: () => void;
}

const PermissionsModal: React.FC<PermissionsModalProps> = ({
  organization,
  components,
  stores,
  dashboardPages,
  onSave,
  onClose
}) => {
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
  const [configData, setConfigData] = useState<any>(null);
  const [storeSearchTerm, setStoreSearchTerm] = useState<string>('');
  const [pageSearchTerm, setPageSearchTerm] = useState<string>('');
  const [componentSearchTerm, setComponentSearchTerm] = useState<string>('');

  useEffect(() => {
    // Load current permissions
    const loadPermissions = async () => {
      try {
        const config = await organizationApi.getConfig(organization.org_id);
        setSelectedStores(config.store_list || []);
        setSelectedPages(config.config_data?.page_list || []);
        setSelectedComponents(config.config_data?.component_list || []);
        setConfigData(config.config_data || {});
      } catch (error) {
        console.error('Error loading permissions:', error);
        // Initialize with default values if no config exists
        setSelectedStores([]);
        setSelectedPages([]);
        setSelectedComponents([]);
        setConfigData({
          pages: [],
          theme: {
            logo_url: "/images/default-logo.svg",
            brand_name: "Dashboard",
            primary_color: "#1976d2",
            secondary_color: "#dc004e"
          },
          features: {
            data_export: true,
            live_monitoring: true,
            multi_store_view: true,
            real_time_updates: true,
            advanced_analytics: true,
            date_range_selection: true
          },
          client_name: organization.name
        });
      }
    };
    loadPermissions();
  }, [organization.org_id, organization.name]);

  // Filter stores based on search term
  const filteredStores = stores.filter(store => 
    store.store_name.toLowerCase().includes(storeSearchTerm.toLowerCase()) ||
    store.org_id.toLowerCase().includes(storeSearchTerm.toLowerCase())
  );

  // Filter pages based on search term
  const filteredPages = dashboardPages.filter(page => 
    page.page_name.toLowerCase().includes(pageSearchTerm.toLowerCase())
  );

  // Filter components based on search term
  const filteredComponents = dashboardPages.filter(page => 
    page.page_name.toLowerCase().includes(componentSearchTerm.toLowerCase()) ||
    page.components.some(component => 
      component.component_name.toLowerCase().includes(componentSearchTerm.toLowerCase()) ||
      (component.component_type && component.component_type.toLowerCase().includes(componentSearchTerm.toLowerCase()))
    )
  ).map(page => ({
    ...page,
    components: componentSearchTerm ? 
      page.components.filter(component => 
        component.component_name.toLowerCase().includes(componentSearchTerm.toLowerCase()) ||
        (component.component_type && component.component_type.toLowerCase().includes(componentSearchTerm.toLowerCase())) ||
        page.page_name.toLowerCase().includes(componentSearchTerm.toLowerCase())
      ) : page.components
  }));

  // Handle select all functionality for stores
  const handleSelectAllStores = () => {
    const allFilteredStoreIds = filteredStores.map(store => store.store_id);
    const allSelected = allFilteredStoreIds.every(id => selectedStores.includes(id));
    
    if (allSelected) {
      setSelectedStores(selectedStores.filter(id => !allFilteredStoreIds.includes(id)));
    } else {
      setSelectedStores([...new Set([...selectedStores, ...allFilteredStoreIds])]);
    }
  };

  // Handle select all functionality for pages
  const handleSelectAllPages = () => {
    const allFilteredPageIds = filteredPages.map(page => page.page_id);
    const allSelected = allFilteredPageIds.every(id => selectedPages.includes(id));
    
    if (allSelected) {
      setSelectedPages(selectedPages.filter(id => !allFilteredPageIds.includes(id)));
    } else {
      setSelectedPages([...new Set([...selectedPages, ...allFilteredPageIds])]);
    }
  };

  // Check if all filtered stores are selected
  const allFilteredStoresSelected = filteredStores.length > 0 && 
    filteredStores.every(store => selectedStores.includes(store.store_id));

  // Check if all filtered pages are selected
  const allFilteredPagesSelected = filteredPages.length > 0 && 
    filteredPages.every(page => selectedPages.includes(page.page_id));

  const handleSave = () => {
    const updatedConfigData = {
      ...configData,
      page_list: selectedPages,
      component_list: selectedComponents
    };
    onSave(selectedStores, updatedConfigData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Manage Permissions - {organization.name}
          </h3>
          
          <div className="grid grid-cols-1 gap-6">
            {/* Stores */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Stores</h4>
              
              {/* Search Box */}
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Search stores by name or organization..."
                  value={storeSearchTerm}
                  onChange={(e) => setStoreSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Select All Checkbox */}
              {filteredStores.length > 0 && (
                <div className="mb-3 pb-2 border-b border-gray-200">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={allFilteredStoresSelected}
                      onChange={handleSelectAllStores}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-800">
                      Select All ({filteredStores.length} stores)
                    </span>
                  </label>
                </div>
              )}

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredStores.length === 0 ? (
                  <div className="text-sm text-gray-500 italic text-center py-4">
                    {storeSearchTerm ? 'No stores found matching your search.' : 'No stores available.'}
                  </div>
                ) : (
                  filteredStores.map((store) => (
                    <label key={store.store_id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedStores.includes(store.store_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStores([...selectedStores, store.store_id]);
                          } else {
                            setSelectedStores(selectedStores.filter(s => s !== store.store_id));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {store.store_name} ({store.org_id})
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Dashboard Pages */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Dashboard Pages</h4>
              
              {/* Search Box */}
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Search pages by name..."
                  value={pageSearchTerm}
                  onChange={(e) => setPageSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              {/* Select All Checkbox */}
              {filteredPages.length > 0 && (
                <div className="mb-3 pb-2 border-b border-gray-200">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={allFilteredPagesSelected}
                      onChange={handleSelectAllPages}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-800">
                      Select All ({filteredPages.length} pages)
                    </span>
                  </label>
                </div>
              )}

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredPages.length === 0 ? (
                  <div className="text-sm text-gray-500 italic text-center py-4">
                    {pageSearchTerm ? 'No pages found matching your search.' : 'No pages available.'}
                  </div>
                ) : (
                  filteredPages.map((page) => (
                    <label key={page.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedPages.includes(page.page_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPages([...selectedPages, page.page_id]);
                          } else {
                            setSelectedPages(selectedPages.filter(p => p !== page.page_id));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {page.page_name} ({page.components.length} components)
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Components */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Components
              </h4>
              
              {/* Search Box */}
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Search components by name, type, or page..."
                  value={componentSearchTerm}
                  onChange={(e) => setComponentSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredComponents.length === 0 ? (
                  <div className="text-sm text-gray-500 italic text-center py-4">
                    {componentSearchTerm ? 'No components found matching your search.' : 'No dashboard pages available. Create pages and components first.'}
                  </div>
                ) : (
                  filteredComponents.map((page) => {
                    const pageComponentKeys = page.components.map(c => `${page.page_id}:${c.component_id}`);
                    const allPageComponentsSelected = pageComponentKeys.length > 0 && 
                      pageComponentKeys.every(key => selectedComponents.includes(key));
                    const somePageComponentsSelected = pageComponentKeys.some(key => selectedComponents.includes(key));
                    
                    return (
                      <div key={page.id} className="border rounded p-3 bg-gray-50">
                        <div className="flex items-center mb-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={allPageComponentsSelected}
                              ref={(input) => {
                                if (input) input.indeterminate = somePageComponentsSelected && !allPageComponentsSelected;
                              }}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Select all components in this page
                                  const newComponents = Array.from(new Set([...selectedComponents, ...pageComponentKeys]));
                                  setSelectedComponents(newComponents);
                                } else {
                                  // Deselect all components in this page
                                  setSelectedComponents(selectedComponents.filter(c => !pageComponentKeys.includes(c)));
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm font-medium text-gray-800">
                              {page.page_name} ({page.components.length} components)
                            </span>
                          </label>
                        </div>
                        {page.components.length === 0 ? (
                          <div className="text-xs text-gray-500 italic ml-6">
                            No components in this page
                          </div>
                        ) : (
                          <div className="ml-6 space-y-1">
                            {page.components.map((component) => {
                              const componentKey = `${page.page_id}:${component.component_id}`;
                              return (
                                <label key={component.id} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedComponents.includes(componentKey)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedComponents([...selectedComponents, componentKey]);
                                      } else {
                                        setSelectedComponents(selectedComponents.filter(c => c !== componentKey));
                                      }
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="ml-2 text-xs text-gray-700">
                                    {component.component_name} {component.component_type && `(${component.component_type})`}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Save Permissions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Organizations;
